import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/apiClient';
import Modal from '../ui/Modal';
import Breadcrumb from '../ui/Breadcrumb';
import Favicon from '../ui/Favicon';
import { IconAlertTriangle, IconBan, IconCheck, IconEdit, IconGlobe, IconKey, IconMoreVertical, IconPlus, IconTrash, IconUsers } from '../ui/icons';
import { moduleColor } from '../ui/modulePalette';

function contactTitle(c) {
  const first = (c.first_name || '').trim();
  const last = (c.last_name || '').trim();
  const company = (c.company_name || '').trim();
  const name = `${last} ${first}`.trim();
  if (name && company) return `${name} (${company})`;
  return name || company || `Contact #${c.id}`;
}

function normalizePhones(raw, { fallbackMain = '', fallbackMobile = '' } = {}) {
  const list = Array.isArray(raw) ? raw : [];
  const out = list
    .filter((p) => p && typeof p === 'object')
    .map((p) => ({ label: String(p.label || '').trim(), number: String(p.number || '').trim() }))
    .filter((p) => p.number)
    .map((p) => ({ label: p.label || 'Téléphone', number: p.number }))
    .slice(0, 8);
  if (out.length) return out;
  const fallback = [];
  const main = String(fallbackMain || '').trim();
  const mobile = String(fallbackMobile || '').trim();
  if (main) fallback.push({ label: 'Professionnel', number: main });
  if (mobile) fallback.push({ label: 'Personnel', number: mobile });
  return fallback;
}

function telHref(num) {
  const raw = String(num || '').trim();
  if (!raw) return '';
  let cleaned = raw.replace(/[^\d+]/g, '');
  if (cleaned.includes('+')) cleaned = `+${cleaned.replace(/\+/g, '')}`;
  return cleaned ? `tel:${cleaned}` : '';
}

function isMessagesOnlyLabel(label) {
  const s = String(label || '').trim().toLowerCase();
  if (!s) return false;
  return s.includes('message');
}

function isCallablePhone(phone) {
  if (!phone || typeof phone !== 'object') return false;
  const number = String(phone.number || '').trim();
  if (!number) return false;
  return !isMessagesOnlyLabel(phone.label);
}

const DEMO_MODULE_CHOICES = [
  { key: 'projects', label: 'Projets' },
  { key: 'resources', label: 'Ressources' },
  { key: 'menus', label: 'Menus' },
  { key: 'scripts', label: 'Scripts' },
  { key: 'team', label: 'Équipe' },
  { key: 'languages', label: 'Langues' },
  { key: 'databases', label: 'Bases de données' },
  { key: 'updates', label: 'Mises à jour' },
  { key: 'backups', label: 'Sauvegarde' },
  { key: 'appointments', label: 'Rendez-vous' },
  { key: 'settings', label: 'Paramètres du site' },
];

const PANEL_SITE_MODULE_CHOICES = [
  { key: 'projects', label: 'Projets' },
  { key: 'articles', label: 'Articles' },
  { key: 'resources', label: 'Ressources' },
  { key: 'gallery', label: 'Galerie' },
  { key: 'menus', label: 'Menus' },
  { key: 'scripts', label: 'Scripts' },
  { key: 'updates', label: 'Mises à jour' },
  { key: 'appointments', label: 'Rendez-vous' },
  { key: 'traffic', label: 'Trafic Web' },
  { key: 'team', label: 'Équipe' },
  { key: 'languages', label: 'Langues' },
  { key: 'databases', label: 'Bases de données' },
  { key: 'backups', label: 'Sauvegarde' },
  { key: 'settings', label: 'Paramètres du site' },
];

function suggestSiteNameFromClient(client) {
  const company = String(client?.company_name || '').trim();
  const first = String(client?.first_name || '').trim();
  const last = String(client?.last_name || '').trim();
  const person = `${first} ${last}`.trim();
  return company || person || '';
}

export default function AdminClientsPage({ onSimulate }) {
  const [state, setState] = useState({ status: 'loading', clients: [], error: '' });
  const [modal, setModal] = useState(null); // { type, ... }
  const [menuClientId, setMenuClientId] = useState(null);
  const [listQuery, setListQuery] = useState('');

  useEffect(() => {
    function onDocDown(e) {
      if (!menuClientId) return;
      const el = e.target;
      if (!(el instanceof HTMLElement)) return;
      if (el.closest('[data-client-menu]')) return;
      setMenuClientId(null);
    }
    function onKey(e) {
      if (e.key === 'Escape') setMenuClientId(null);
    }
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuClientId]);

  async function openPortalAccess(client) {
    setModal({
      type: 'access',
      client,
      status: 'loading',
      portal: null,
      details: null,
      form: {
        email: (client.email || '').trim(),
        display_name: contactTitle(client),
        is_active: true,
      },
      temp_password: '',
      saving: false,
      resetting_password: false,
      invalidating: false,
      error: '',
    });
    const res = await api.adminClientAccess(client.id);
    if (!res.ok) {
      setModal((m) =>
        m?.type === 'access' ? { ...m, status: 'error', error: res.error || 'Erreur', details: res.details || null } : m
      );
      return;
    }
    const portal = res.portal_user || null;
    setModal((m) =>
      m?.type === 'access'
        ? {
            ...m,
            status: 'ready',
            portal,
            details: null,
            form: {
              email: portal?.email || (client.email || '').trim(),
              display_name: portal?.display_name || contactTitle(client),
              is_active: portal ? Boolean(portal.is_active) : true,
            },
            temp_password: '',
            error: '',
          }
        : m
    );
  }

  async function openDemoAccount(client) {
    setModal({
      type: 'demo',
      client,
      status: 'loading',
      is_enabled: false,
      label: '',
      module_keys: [],
      token: '',
      saving: false,
      error: '',
    });
    const res = await api.adminClientDemo(client.id);
    if (!res.ok) {
      setModal((m) => (m?.type === 'demo' ? { ...m, status: 'error', error: res.error || 'Erreur' } : m));
      return;
    }
    const d = res.demo || {};
    setModal((m) =>
      m?.type === 'demo'
        ? {
            ...m,
            status: 'ready',
            is_enabled: Boolean(d.is_enabled),
            label: String(d.label || ''),
            module_keys: Array.isArray(d.module_keys) ? d.module_keys.map((x) => String(x || '')).filter(Boolean) : [],
            token: String(d.token || ''),
            error: '',
          }
        : m
    );
  }

  const refresh = () => {
    let cancelled = false;
    api
      .adminClients()
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'error', clients: [], error: res.error || 'Impossible de charger' });
          return;
        }
        setState({ status: 'ready', clients: res.clients || [], error: '' });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'error', clients: [], error: 'Impossible de charger' });
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => refresh(), []);

  const sorted = useMemo(() => {
    if (state.status !== 'ready') return [];
    return [...state.clients].sort((a, b) => contactTitle(a).localeCompare(contactTitle(b), 'fr'));
  }, [state.clients, state.status]);

  const visible = useMemo(() => {
    const q = String(listQuery || '')
      .trim()
      .toLowerCase();
    if (!q) return sorted;
    return sorted.filter((c) => {
      const title = contactTitle(c).toLowerCase();
      const email = String(c?.email || '').toLowerCase();
      const website = String(c?.website || '').toLowerCase();
      const siteNames = (c?.sites || []).map((s) => String(s?.name || '').toLowerCase());
      return title.includes(q) || email.includes(q) || website.includes(q) || siteNames.some((x) => x.includes(q));
    });
  }, [sorted, listQuery]);

  async function saveClient(client) {
    const isEdit = Boolean(client.id);
    const phones = normalizePhones(client?.phones, { fallbackMain: client?.phone_main, fallbackMobile: client?.phone_mobile });
    const payload = { ...(client || {}), phones };
    const res = isEdit ? await api.adminClientUpdate(payload) : await api.adminClientCreate(payload);
    if (!res.ok) return res;
    refresh();
    return res;
  }

  async function createSiteForClient({ client_id, name, url, send_email }) {
    const res = await api.adminSiteCreate({ client_id, name, url, send_email: Boolean(send_email) });
    if (!res.ok) return res;
    refresh();
    window.dispatchEvent(new CustomEvent('omniscope:sitesChanged'));
    return res;
  }

  async function createClientAndSiteWithModules({ clientDraft, siteName, siteUrl, sendEmail, moduleKeys }) {
    const clientPayload = {
      ...(clientDraft || {}),
      phones: normalizePhones(clientDraft?.phones, {
        fallbackMain: clientDraft?.phone_main,
        fallbackMobile: clientDraft?.phone_mobile,
      }),
    };
    const clientRes = await api.adminClientCreate(clientPayload);
    if (!clientRes.ok || !clientRes.client) {
      return {
        ok: false,
        stage: 'client',
        error: clientRes.error || 'Création du contact impossible',
      };
    }

    const createdClient = clientRes.client;
    const siteRes = await api.adminSiteCreate({
      client_id: Number(createdClient.id),
      name: String(siteName || '').trim(),
      url: String(siteUrl || '').trim(),
      send_email: Boolean(sendEmail),
    });
    if (!siteRes.ok || !siteRes.site) {
      refresh();
      return {
        ok: false,
        stage: 'site',
        client: createdClient,
        error: siteRes.error || 'Création du site impossible',
      };
    }

    const normalizedKeys = (Array.isArray(moduleKeys) ? moduleKeys : [])
      .map((x) => String(x || '').trim())
      .filter(Boolean);
    const enabledKeys = Array.from(new Set(['dashboard', ...normalizedKeys]));
    const modulesRes = await api.adminSiteModulesUpdate({
      site_id: Number(siteRes.site.id),
      enabled_keys: enabledKeys,
    });
    if (!modulesRes.ok) {
      refresh();
      window.dispatchEvent(new CustomEvent('omniscope:sitesChanged'));
      return {
        ok: false,
        stage: 'modules',
        client: createdClient,
        site: siteRes.site,
        error: modulesRes.error || 'Configuration des modules impossible',
      };
    }

    refresh();
    window.dispatchEvent(new CustomEvent('omniscope:sitesChanged'));
    return {
      ok: true,
      client: createdClient,
      site: siteRes.site,
      email: siteRes.email || null,
    };
  }

  async function deleteClient({ id, keep_sites }) {
    const res = await api.adminClientDelete({ id, keep_sites });
    if (!res.ok) return res;
    refresh();
    return res;
  }

	  return (
	    <div className="Page">
	      <header className="PageHeader">
	        <div className="PageHeaderBar">
	          <Breadcrumb items={[{ label: 'Contacts' }]} />
	        </div>
	      </header>

      {state.status === 'error' ? (
        <div className="Card">
          <div className="Error" role="alert">
            {state.error}
          </div>
        </div>
      ) : null}

      <div className="Card">
        <div className="Row" style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gap: 8, minWidth: 260, flex: 1 }}>
            <div className="TileTitle">Liste</div>
            <input
              className="Input"
              value={listQuery}
              onChange={(e) => setListQuery(e.target.value)}
              placeholder="Rechercher un contact…"
              aria-label="Rechercher un contact"
            />
          </div>
          <button
            type="button"
            className="Button"
            onClick={() =>
              setModal({
                type: 'edit-client',
                client: {
                  first_name: '',
                  last_name: '',
	                  company_name: '',
	                  siren: '',
	                  siret: '',
	                  email: '',
	                  phones: [],
	                  phone_main: '',
	                  phone_mobile: '',
                  address_line1: '',
                  address_line2: '',
                  postal_code: '',
                  city: '',
                  country: '',
                  website: '',
                  notes: '',
                  is_active: true,
                },
                error: '',
                saving: false,
              })
            }
          >
	            <span className="ButtonInline">
	              <IconPlus /> Nouveau contact
	            </span>
	          </button>
        </div>

        {state.status === 'loading' ? <div className="Muted">Chargement…</div> : null}
	        {state.status === 'ready' && visible.length === 0 ? <div className="Muted">{state.clients.length === 0 ? 'Aucun contact.' : 'Aucun contact ne correspond.'}</div> : null}

        {state.status === 'ready' ? (
          <div className="List" role="list">
	            {visible.map((c) => (
	              <div className="ListItem" role="listitem" key={c.id}>
	                <div>
	                  <div className="UserName">{contactTitle(c)}</div>
	                  <div className="Muted">
	                    {(c.email || '').trim() ? c.email : '—'}
	                  </div>
	                  <div className="Badges" style={{ marginTop: 10, justifyContent: 'flex-start' }}>
	                    {(c.sites || []).length ? (
	                      (c.sites || []).map((s) => (
                        <span key={s.id} className="SitePill">
                          <Favicon url={s.url} faviconUrl={s.favicon_url} />
                          <span>{s.name}</span>
                        </span>
                      ))
                    ) : (
                      <span className="Badge">Aucun site</span>
                    )}
                  </div>
	                </div>
	                <div className="Badges AdminListItemActions">
	                  {(() => {
	                    const phones = normalizePhones(c.phones, { fallbackMain: c.phone_main, fallbackMobile: c.phone_mobile });
	                    const usable = phones.filter(isCallablePhone);
	                    if (usable.length === 1) {
	                      const href = telHref(usable[0].number);
	                      return href ? (
	                        <a className="Button Button--ghost" href={href} title={`Appeler (${usable[0].label})`}>
	                          Appeler
	                        </a>
	                      ) : (
	                        <button type="button" className="Button Button--ghost" disabled>
	                          Appeler
	                        </button>
	                      );
	                    }
	                    return (
	                      <button
	                        type="button"
	                        className="Button Button--ghost"
	                        disabled={usable.length === 0}
	                        onClick={() => setModal({ type: 'call', contact: c, phones: usable })}
	                        title={usable.length ? 'Choisir un numéro' : 'Aucun numéro'}
	                      >
	                        Appeler
	                      </button>
	                    );
	                  })()}
	                  <span className="ContextMenu" data-client-menu>
	                    <button
	                      type="button"
	                      className="Button Button--ghost"
                      onClick={() => setMenuClientId((id) => (Number(id) === Number(c.id) ? null : c.id))}
                      title="Actions"
                      aria-haspopup="menu"
                      aria-expanded={Number(menuClientId) === Number(c.id) ? 'true' : 'false'}
                    >
                      <span className="ButtonInline">
                        <IconMoreVertical /> Actions
                      </span>
	                    </button>
	                    {Number(menuClientId) === Number(c.id) ? (
	                      <div className="Menu" role="menu" aria-label={`Actions contact ${contactTitle(c)}`}>
	                        <button
	                          type="button"
	                          className="MenuItem"
	                          role="menuitem"
	                          onClick={() => {
	                            setMenuClientId(null);
	                            openPortalAccess(c);
	                          }}
	                        >
	                          <span className="ButtonInline">
	                            <IconKey /> Accès
	                          </span>
	                        </button>
		                        <button
		                          type="button"
		                          className="MenuItem"
		                          role="menuitem"
		                          disabled={!((c.sites || []).length)}
	                          onClick={() => {
	                            setMenuClientId(null);
	                            const sites = c.sites || [];
	                            if (sites.length === 1) {
	                              onSimulate?.({ client: c, sites, siteId: sites[0].id });
	                              return;
	                            }
	                            setModal({ type: 'simulate', client: c, sites, siteId: sites[0]?.id ?? null });
	                          }}
	                          title={(c.sites || []).length ? 'Simuler la vue client' : 'Aucun site à simuler'}
	                        >
		                          <span className="ButtonInline">
		                            <IconUsers /> Simuler
		                          </span>
		                        </button>
                        <button
                          type="button"
                          className="MenuItem"
                          role="menuitem"
                          onClick={() => {
                            setMenuClientId(null);
                            openDemoAccount(c);
                          }}
                        >
                          <span className="ButtonInline">
                            <IconKey /> Compte démo
                          </span>
                        </button>
		                        <div className="MenuSeparator" role="separator" />
		                        <button
		                          type="button"
		                          className="MenuItem"
		                          role="menuitem"
	                          onClick={() => {
	                            setMenuClientId(null);
	                            const phones = normalizePhones(c.phones, { fallbackMain: c.phone_main, fallbackMobile: c.phone_mobile });
	                            setModal({ type: 'edit-client', client: { ...c, phones }, error: '', saving: false });
	                          }}
	                        >
                          <span className="ButtonInline">
                            <IconEdit /> Modifier
                          </span>
                        </button>
                        <button
                          type="button"
                          className="MenuItem"
                          role="menuitem"
                          onClick={() => {
                            setMenuClientId(null);
                            setModal({ type: 'add-site', client: c, name: '', url: '', send_email: false, error: '', saving: false });
                          }}
                        >
	                          <span className="ButtonInline">
	                            <IconGlobe /> Ajouter un site
	                          </span>
	                        </button>
	                        <div className="MenuSeparator" role="separator" />
	                        <button
	                          type="button"
	                          className="MenuItem MenuItem--danger"
	                          role="menuitem"
                          onClick={() => {
                            setMenuClientId(null);
                            setModal({ type: 'delete-step1', client: c });
                          }}
                        >
                          <span className="ButtonInline">
                            <IconTrash /> Supprimer
                          </span>
                        </button>
                      </div>
                    ) : null}
                  </span>
                </div>
              </div>
            ))}
          </div>
	        ) : null}
	      </div>

	      {modal?.type === 'call' ? (
	        <Modal
	          title="Appeler"
	          icon={<IconUsers />}
	          onClose={() => setModal(null)}
	          footer={
	            <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
	              Fermer
	            </button>
	          }
	        >
	          <div className="TitleMd" style={{ marginBottom: 6 }}>
	            {contactTitle(modal.contact)}
	          </div>
	          <div className="List" role="list">
	            {(modal.phones || []).map((p, idx) => {
	              const href = telHref(p.number);
	              return (
	                <a key={`${idx}_${p.number}`} className="ListItem" href={href} onClick={() => setModal(null)}>
	                  <div style={{ minWidth: 0 }}>
	                    <div className="UserName">{p.label}</div>
	                    <div className="Muted">{p.number}</div>
	                  </div>
	                </a>
	              );
	            })}
	          </div>
	        </Modal>
	      ) : null}

	      {modal?.type === 'access' ? (
	        <Modal
	          title="Accès au panel"
	          icon={<IconKey />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" disabled={modal.saving || modal.invalidating} onClick={() => setModal(null)}>
                Fermer
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                disabled={modal.status !== 'ready' || modal.invalidating || !modal.portal}
                onClick={async () => {
                  setModal((m) => ({ ...m, invalidating: true, error: '' }));
                  const res = await api.adminClientAccessInvalidate({ client_id: modal.client.id });
                  if (!res.ok) {
                    setModal((m) => ({ ...m, invalidating: false, error: res.error || 'Erreur', details: res.details || null }));
                    return;
                  }
                  setModal((m) => ({ ...m, invalidating: false, portal: res.portal_user || m.portal }));
                }}
                title="Déconnecte tous les appareils connectés"
              >
                {modal.invalidating ? 'Déconnexion…' : 'Déconnecter tous les appareils'}
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.status !== 'ready' || modal.saving || modal.resetting_password}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: '', temp_password: '' }));
                  const payload = {
                    client_id: modal.client.id,
                    email: modal.form.email,
                    display_name: modal.form.display_name,
                    is_active: modal.form.is_active,
                  };
                  const res = await api.adminClientAccessUpsert(payload);
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur', details: res.details || null }));
                    return;
                  }
                  setModal((m) => ({
                    ...m,
                    saving: false,
                    portal: res.portal_user || m.portal,
                    details: null,
                    temp_password: String(res.temp_password || ''),
                  }));
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
              <button
                type="button"
                className="Button Button--ghost Button--danger"
                disabled={modal.status !== 'ready' || modal.resetting_password || !modal.portal}
                onClick={async () => {
                  setModal((m) => ({ ...m, resetting_password: true, error: '', temp_password: '' }));
                  const payload = {
                    client_id: modal.client.id,
                    email: modal.form.email,
                    display_name: modal.form.display_name,
                    is_active: modal.form.is_active,
                    reset_password: true,
                  };
                  const res = await api.adminClientAccessUpsert(payload);
                  if (!res.ok) {
                    setModal((m) => ({ ...m, resetting_password: false, error: res.error || 'Erreur', details: res.details || null }));
                    return;
                  }
                  setModal((m) => ({
                    ...m,
                    resetting_password: false,
                    portal: res.portal_user || m.portal,
                    details: null,
                    temp_password: String(res.temp_password || ''),
                  }));
                }}
                title="Génère un mot de passe temporaire et invalide les sessions"
              >
                {modal.resetting_password ? 'Réinitialisation…' : 'Réinitialiser le mot de passe'}
              </button>
            </>
          }
        >
          <div className="TitleMd" style={{ marginBottom: 6 }}>
            {contactTitle(modal.client)}
          </div>
          <div className="Muted" style={{ marginBottom: 10 }}>
            Crée / modifie l’utilisateur “client” pour se connecter au panel. Un mot de passe temporaire est généré à la création (ou via réinitialisation) et devra être changé à la première connexion.
          </div>

          {modal.status === 'loading' ? <div className="Muted">Chargement…</div> : null}
          {modal.status === 'error' ? (
            <div className="Error" role="alert">
              {modal.error || 'Erreur'}
            </div>
          ) : null}

          {modal.status === 'ready' ? (
            <>
	          <div className="FormGrid">
                <div className="Field">
                  <label className="Label" htmlFor="acc-email">
                    Email de connexion
                  </label>
                  <input
                    id="acc-email"
                    className="Input"
                    value={modal.form.email}
                    onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, email: e.target.value } }))}
                  />
                </div>
                <div className="Field">
                  <label className="Label" htmlFor="acc-name">
                    Nom affiché
                  </label>
                  <input
                    id="acc-name"
                    className="Input"
                    value={modal.form.display_name}
                    onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, display_name: e.target.value } }))}
                  />
                </div>
              </div>

	              <div className="FormGrid">
	                <div className="Field">
	                  <label className="Label">Statut</label>
	                  <div className="Badges" style={{ justifyContent: 'flex-start' }}>
	                    <button
	                      type="button"
	                      className={`TogglePill ${modal.form.is_active ? 'is-on' : ''}`}
	                      onClick={() => setModal((m) => ({ ...m, form: { ...m.form, is_active: true } }))}
	                    >
	                      <IconCheck /> Actif
	                    </button>
	                    <button
	                      type="button"
	                      className={`TogglePill ${!modal.form.is_active ? 'is-on' : ''}`}
	                      onClick={() => setModal((m) => ({ ...m, form: { ...m.form, is_active: false } }))}
	                    >
	                      <IconBan /> Désactivé
	                    </button>
	                  </div>
	                </div>
	              </div>

              {modal.temp_password ? (
                <div className="Notice" style={{ marginTop: 10 }}>
                  <div className="TitleMd" style={{ marginBottom: 6 }}>
                    Mot de passe temporaire
                  </div>
                  <div className="InputRow">
                    <input className="Input" readOnly value={modal.temp_password} />
                    <button
                      type="button"
                      className="Button Button--ghost"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(String(modal.temp_password || ''));
                          setModal((m) => ({ ...m, error: '' }));
                        } catch {
                          setModal((m) => ({ ...m, error: 'Impossible de copier.' }));
                        }
                      }}
                    >
                      Copier
                    </button>
                  </div>
                </div>
              ) : null}

              {modal.portal ? (
                <div className="Hint">
                  Compte existant: <code>{modal.portal.email}</code>
                </div>
              ) : (
                <div className="Hint">Aucun compte n’est encore créé pour ce client.</div>
              )}

              {modal.error ? (
                <div className="Error" role="alert" style={{ marginTop: 10 }}>
                  {modal.error}
                </div>
              ) : null}

              {modal.details ? (
                <div className="Notice" style={{ marginTop: 10 }}>
                  <div className="TitleMd" style={{ marginBottom: 6 }}>
                    Détails (debug)
                  </div>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'var(--mono)', fontSize: 12 }}>
                    {JSON.stringify(modal.details, null, 2)}
                  </pre>
                </div>
              ) : null}
            </>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'demo' ? (
        <Modal
          title="Compte démo"
          icon={<IconKey />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" disabled={modal.saving} onClick={() => setModal(null)}>
                Fermer
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.status !== 'ready' || modal.saving}
                onClick={async () => {
                  setModal((m) => (m?.type === 'demo' ? { ...m, saving: true, error: '' } : m));
                  const res = await api.adminClientDemoUpsert({
                    client_id: modal.client.id,
                    is_enabled: Boolean(modal.is_enabled),
                    label: modal.label,
                    module_keys: modal.module_keys,
                  });
                  if (!res.ok) {
                    setModal((m) => (m?.type === 'demo' ? { ...m, saving: false, error: res.error || 'Erreur' } : m));
                    return;
                  }
                  const d = res.demo || {};
                  setModal((m) =>
                    m?.type === 'demo'
                      ? {
                          ...m,
                          saving: false,
                          status: 'ready',
                          is_enabled: Boolean(d.is_enabled),
                          label: String(d.label || ''),
                          module_keys: Array.isArray(d.module_keys) ? d.module_keys.map((x) => String(x || '')).filter(Boolean) : [],
                          token: String(d.token || ''),
                          error: '',
                        }
                      : m
                  );
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="TitleMd" style={{ marginBottom: 6 }}>
            {contactTitle(modal.client)}
          </div>

          {modal.status === 'loading' ? <div className="Muted">Chargement…</div> : null}
          {modal.status === 'error' ? (
            <div className="Error" role="alert">
              {modal.error || 'Erreur'}
            </div>
          ) : null}

          {modal.status === 'ready' ? (
            <>
              <div className="Field">
                <label className="Label">Activer la démo</label>
                <label className="ButtonInline" style={{ gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={Boolean(modal.is_enabled)}
                    onChange={(e) => setModal((m) => (m?.type === 'demo' ? { ...m, is_enabled: Boolean(e.target.checked) } : m))}
                  />
                  <span>Compte démo actif</span>
                </label>
              </div>

              <div className="Field">
                <label className="Label">Nom (optionnel)</label>
                <input
                  className="Input"
                  value={String(modal.label || '')}
                  onChange={(e) => setModal((m) => (m?.type === 'demo' ? { ...m, label: e.target.value } : m))}
                  placeholder="ex: Démo UI/UX"
                />
              </div>

              <div className="Field">
                <label className="Label">Modules visibles</label>
                <div style={{ display: 'grid', gap: 8 }}>
                  {DEMO_MODULE_CHOICES.map((m) => {
                    const checked = (modal.module_keys || []).includes(m.key);
                    return (
                      <label key={m.key} className="ButtonInline" style={{ gap: 10, justifyContent: 'space-between' }}>
                        <span className="ButtonInline" style={{ gap: 10 }}>
                          <span
                            aria-hidden="true"
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              background: moduleColor(m.key),
                              display: 'inline-block',
                            }}
                          />
                          <span>{m.label}</span>
                        </span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = Boolean(e.target.checked);
                            setModal((cur) => {
                              if (!cur || cur.type !== 'demo') return cur;
                              const set = new Set((cur.module_keys || []).map((x) => String(x || '')).filter(Boolean));
                              if (next) set.add(m.key);
                              else set.delete(m.key);
                              return { ...cur, module_keys: Array.from(set) };
                            });
                          }}
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              {modal.token ? (
                <div className="Field">
                  <label className="Label">Lien démo</label>
                  <div className="InputRow">
                    <input
                      className="Input"
                      readOnly
                      value={`${window.location.origin}${window.location.pathname}?demo=${encodeURIComponent(String(modal.token))}`}
                    />
                    <button
                      type="button"
                      className="Button Button--ghost"
                      onClick={async () => {
                        const url = `${window.location.origin}${window.location.pathname}?demo=${encodeURIComponent(String(modal.token))}`;
                        try {
                          await navigator.clipboard.writeText(url);
                          setModal((m) => (m?.type === 'demo' ? { ...m, error: '' } : m));
                        } catch {
                          setModal((m) => (m?.type === 'demo' ? { ...m, error: 'Impossible de copier le lien.' } : m));
                        }
                      }}
                    >
                      Copier
                    </button>
                  </div>
                </div>
              ) : null}

              {modal.error ? (
                <div className="Error" role="alert">
                  {modal.error}
                </div>
              ) : null}
            </>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'edit-client' ? (
        <Modal
	          title={modal.client.id ? 'Modifier un contact' : 'Créer un contact'}
	          icon={<IconUsers />}
	          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={async () => {
                  const wasCreate = !Boolean(modal.client?.id);
                  const websiteRaw = String(modal.client?.website || '').trim();
                  if (wasCreate && websiteRaw) {
                    setModal({
                      type: 'create-site-from-website-confirm',
                      clientDraft: { ...(modal.client || {}) },
                      siteName: suggestSiteNameFromClient(modal.client),
                      siteUrl: websiteRaw,
                      error: '',
                      saving: false,
                    });
                    return;
                  }
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await saveClient(modal.client);
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  if (wasCreate && res.client) {
                    setModal({ type: 'post-create-site', client: res.client });
                    return;
                  }
                  setModal(null);
                }}
              >
                {modal.saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label" htmlFor="c-first">
                Prénom
              </label>
              <input
                id="c-first"
                className="Input"
                value={modal.client.first_name || ''}
                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, first_name: e.target.value } }))}
              />
            </div>
            <div className="Field">
              <label className="Label" htmlFor="c-last">
                Nom
              </label>
              <input
                id="c-last"
                className="Input"
                value={modal.client.last_name || ''}
                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, last_name: e.target.value } }))}
              />
            </div>
            <div className="Field">
              <label className="Label" htmlFor="c-company">
                Société
              </label>
              <input
                id="c-company"
                className="Input"
                value={modal.client.company_name || ''}
                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, company_name: e.target.value } }))}
              />
            </div>
	            <div className="Field">
	              <label className="Label" htmlFor="c-siren">
	                SIREN
	              </label>
	              <input
	                id="c-siren"
	                className="Input"
	                inputMode="numeric"
	                value={modal.client.siren || ''}
	                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, siren: e.target.value } }))}
	              />
	            </div>
	            <div className="Field">
	              <label className="Label" htmlFor="c-siret">
	                SIRET
	              </label>
	              <input
	                id="c-siret"
	                className="Input"
	                inputMode="numeric"
	                value={modal.client.siret || ''}
	                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, siret: e.target.value } }))}
	              />
	            </div>
            <div className="Field">
              <label className="Label" htmlFor="c-email">
                Email
              </label>
              <input
                id="c-email"
                className="Input"
                type="email"
                value={modal.client.email || ''}
                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, email: e.target.value } }))}
              />
            </div>
	            <div className="Field">
	              <label className="Label" htmlFor="c-website">
	                Site (vitrine)
	              </label>
	              <input
	                id="c-website"
	                className="Input"
	                value={modal.client.website || ''}
	                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, website: e.target.value } }))}
	              />
	            </div>
	          </div>
	
	          <div className="Field">
	            <label className="Label">Téléphones</label>
	            <div className="Card" style={{ padding: 12 }}>
	              {(Array.isArray(modal.client.phones) ? modal.client.phones : []).length === 0 ? (
	                <div className="Muted">Aucun numéro.</div>
	              ) : null}
	              {(Array.isArray(modal.client.phones) ? modal.client.phones : []).map((p, idx) => {
	                const presets = ['Personnel', 'Professionnel', 'Fixe (bureau)', 'Messages seulement'];
	                const label = String(p?.label || '').trim();
	                const isPreset = presets.includes(label);
	                const selectValue = isPreset ? label : 'Autre';
	                return (
	                  <div key={idx} className="Row" style={{ alignItems: 'end', gap: 10, marginBottom: 10 }}>
	                    <div style={{ flex: 1, minWidth: 160 }}>
	                      <label className="Label">Label</label>
	                      <select
	                        className="Select"
	                        value={selectValue}
	                        onChange={(e) => {
	                          const v = String(e.target.value || '');
	                          setModal((m) => {
	                            const list = Array.isArray(m.client.phones) ? [...m.client.phones] : [];
	                            const cur = list[idx] && typeof list[idx] === 'object' ? { ...list[idx] } : { label: '', number: '' };
	                            cur.label = v === 'Autre' ? (isPreset ? '' : cur.label) : v;
	                            list[idx] = cur;
	                            return { ...m, client: { ...m.client, phones: list } };
	                          });
	                        }}
	                      >
	                        {presets.map((x) => (
	                          <option key={x} value={x}>
	                            {x}
	                          </option>
	                        ))}
	                        <option value="Autre">Autre…</option>
	                      </select>
	                    </div>
	                    {selectValue === 'Autre' ? (
	                      <div style={{ flex: 1, minWidth: 160 }}>
	                        <label className="Label">Label personnalisé</label>
	                        <input
	                          className="Input"
	                          value={label}
	                          onChange={(e) => {
	                            const v = e.target.value;
	                            setModal((m) => {
	                              const list = Array.isArray(m.client.phones) ? [...m.client.phones] : [];
	                              const cur = list[idx] && typeof list[idx] === 'object' ? { ...list[idx] } : { label: '', number: '' };
	                              cur.label = v;
	                              list[idx] = cur;
	                              return { ...m, client: { ...m.client, phones: list } };
	                            });
	                          }}
	                        />
	                      </div>
	                    ) : null}
	                    <div style={{ flex: 1, minWidth: 180 }}>
	                      <label className="Label">Numéro</label>
	                      <input
	                        className="Input"
	                        value={String(p?.number || '')}
	                        onChange={(e) => {
	                          const v = e.target.value;
	                          setModal((m) => {
	                            const list = Array.isArray(m.client.phones) ? [...m.client.phones] : [];
	                            const cur = list[idx] && typeof list[idx] === 'object' ? { ...list[idx] } : { label: '', number: '' };
	                            cur.number = v;
	                            list[idx] = cur;
	                            return { ...m, client: { ...m.client, phones: list } };
	                          });
	                        }}
	                      />
	                    </div>
	                    <button
	                      type="button"
	                      className="Button Button--ghost Button--danger"
	                      onClick={() => {
	                        setModal((m) => {
	                          const list = Array.isArray(m.client.phones) ? [...m.client.phones] : [];
	                          list.splice(idx, 1);
	                          return { ...m, client: { ...m.client, phones: list } };
	                        });
	                      }}
	                      title="Supprimer le numéro"
	                    >
	                      <IconTrash />
	                    </button>
	                  </div>
	                );
	              })}
	              <button
	                type="button"
	                className="Button Button--ghost"
	                onClick={() =>
	                  setModal((m) => {
	                    const list = Array.isArray(m.client.phones) ? [...m.client.phones] : [];
	                    list.push({ label: 'Professionnel', number: '' });
	                    return { ...m, client: { ...m.client, phones: list } };
	                  })
	                }
	              >
	                <span className="ButtonInline">
	                  <IconPlus /> Ajouter un numéro
	                </span>
	              </button>
	            </div>
	          </div>

          <div className="Field">
            <label className="Label" htmlFor="c-address1">
              Adresse
            </label>
            <input
              id="c-address1"
              className="Input"
              value={modal.client.address_line1 || ''}
              onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, address_line1: e.target.value } }))}
            />
          </div>
          <div className="Field">
            <label className="Label" htmlFor="c-address2">
              Complément
            </label>
            <input
              id="c-address2"
              className="Input"
              value={modal.client.address_line2 || ''}
              onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, address_line2: e.target.value } }))}
            />
          </div>

          <div className="FormGrid">
            <div className="Field">
              <label className="Label" htmlFor="c-zip">
                Code postal
              </label>
              <input
                id="c-zip"
                className="Input"
                value={modal.client.postal_code || ''}
                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, postal_code: e.target.value } }))}
              />
            </div>
            <div className="Field">
              <label className="Label" htmlFor="c-city">
                Ville
              </label>
              <input
                id="c-city"
                className="Input"
                value={modal.client.city || ''}
                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, city: e.target.value } }))}
              />
            </div>
            <div className="Field">
              <label className="Label" htmlFor="c-country">
                Pays
              </label>
              <input
                id="c-country"
                className="Input"
                value={modal.client.country || ''}
                onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, country: e.target.value } }))}
              />
            </div>
	            <div className="Field">
	              <label className="Label">Statut</label>
	              <div className="Badges" style={{ justifyContent: 'flex-start' }}>
	                <button
	                  type="button"
	                  className={`TogglePill ${modal.client.is_active ? 'is-on' : ''}`}
	                  onClick={() => setModal((m) => ({ ...m, client: { ...m.client, is_active: true } }))}
	                >
	                  <IconCheck /> Actif
	                </button>
	                <button
	                  type="button"
	                  className={`TogglePill ${!modal.client.is_active ? 'is-on' : ''}`}
	                  onClick={() => setModal((m) => ({ ...m, client: { ...m.client, is_active: false } }))}
	                >
	                  <IconBan /> Inactif
	                </button>
	              </div>
	            </div>
	          </div>

          <div className="Field">
            <label className="Label" htmlFor="c-notes">
              Notes
            </label>
            <textarea
              id="c-notes"
              className="Input"
              rows={4}
              value={modal.client.notes || ''}
              onChange={(e) => setModal((m) => ({ ...m, client: { ...m.client, notes: e.target.value } }))}
            />
          </div>

          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'create-site-from-website-confirm' ? (
        <Modal
          title="Créer aussi un site panel ?"
          icon={<IconGlobe />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" disabled={modal.saving} onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                disabled={modal.saving}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await saveClient(modal.clientDraft);
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Création du contact impossible' }));
                    return;
                  }
                  setModal(null);
                }}
              >
                Non, créer seulement le contact
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={() =>
                  setModal({
                    type: 'create-site-from-website-modules',
                    clientDraft: { ...(modal.clientDraft || {}) },
                    siteName: String(modal.siteName || '').trim(),
                    siteUrl: String(modal.siteUrl || '').trim(),
                    send_email: true,
                    selected_keys: ['projects', 'resources', 'settings'],
                    saving: false,
                    error: '',
                  })
                }
              >
                Oui, choisir les modules
              </button>
            </>
          }
        >
          <div className="Muted">
            Un site vitrine a été renseigné pour ce contact.
            <br />
            Veux-tu aussi créer un site panel avec cette URL : <strong>{String(modal.siteUrl || '')}</strong> ?
          </div>
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'create-site-from-website-modules' ? (
        <Modal
          title="Modules du nouveau site"
          icon={<IconGlobe />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" disabled={modal.saving} onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await createClientAndSiteWithModules({
                    clientDraft: modal.clientDraft,
                    siteName: modal.siteName,
                    siteUrl: modal.siteUrl,
                    sendEmail: Boolean(modal.send_email),
                    moduleKeys: modal.selected_keys,
                  });
                  if (!res.ok) {
                    const stageLabel =
                      res.stage === 'client'
                        ? 'contact'
                        : res.stage === 'site'
                        ? 'site'
                        : res.stage === 'modules'
                        ? 'modules'
                        : 'création';
                    setModal((m) => ({
                      ...m,
                      saving: false,
                      error: `Échec sur ${stageLabel}: ${res.error || 'erreur inconnue'}`,
                    }));
                    return;
                  }
                  if (res.email && res.email.ok === false) {
                    setModal({ type: 'email-warning', error: res.email.error || 'Email non envoyé' });
                    return;
                  }
                  setModal(null);
                }}
              >
                {modal.saving ? 'Création…' : 'Créer contact + site + modules'}
              </button>
            </>
          }
        >
          <div className="FormGrid">
            <div className="Field">
              <label className="Label">Nom du site panel</label>
              <input
                className="Input"
                value={String(modal.siteName || '')}
                onChange={(e) => setModal((m) => ({ ...m, siteName: e.target.value }))}
                placeholder="Nom du site"
              />
            </div>
            <div className="Field">
              <label className="Label">URL vitrine</label>
              <input
                className="Input"
                value={String(modal.siteUrl || '')}
                onChange={(e) => setModal((m) => ({ ...m, siteUrl: e.target.value }))}
                placeholder="https://..."
              />
            </div>
          </div>

          <label className="ButtonInline" style={{ gap: 10, marginTop: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(modal.send_email)}
              onChange={(e) => setModal((m) => ({ ...m, send_email: Boolean(e.target.checked) }))}
              disabled={modal.saving}
            />
            <span>Envoyer un email de liaison au contact</span>
          </label>

          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Modules à activer</label>
            <div style={{ display: 'grid', gap: 8 }}>
              {PANEL_SITE_MODULE_CHOICES.map((m) => {
                const checked = (modal.selected_keys || []).includes(m.key);
                return (
                  <label key={m.key} className="ButtonInline" style={{ gap: 10, justifyContent: 'space-between' }}>
                    <span className="ButtonInline" style={{ gap: 10 }}>
                      <span
                        aria-hidden="true"
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          background: moduleColor(m.key),
                          display: 'inline-block',
                        }}
                      />
                      <span>{m.label}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const next = Boolean(e.target.checked);
                        setModal((cur) => {
                          if (!cur || cur.type !== 'create-site-from-website-modules') return cur;
                          const set = new Set((cur.selected_keys || []).map((x) => String(x || '')).filter(Boolean));
                          if (next) set.add(m.key);
                          else set.delete(m.key);
                          return { ...cur, selected_keys: Array.from(set) };
                        });
                      }}
                    />
                  </label>
                );
              })}
            </div>
          </div>

          {modal.error ? (
            <div className="Error" role="alert" style={{ marginTop: 10 }}>
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'post-create-site' ? (
        <Modal
          title="Créer un site ?"
          icon={<IconGlobe />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Plus tard
              </button>
              <button
                type="button"
                className="Button"
                onClick={() => {
                  const c = modal.client;
                  setModal({ type: 'add-site', client: c, name: '', url: '', send_email: false, error: '', saving: false });
                }}
              >
                Créer et lier un site
              </button>
            </>
          }
        >
          <div className="Muted">
            Contact créé : <strong>{contactTitle(modal.client)}</strong>. Veux-tu créer et lier un nouveau site maintenant ?
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'email-warning' ? (
        <Modal
          title="Email non envoyé"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
              Fermer
            </button>
          }
        >
          <div className="Error" role="alert">
            {modal.error || 'Email non envoyé.'}
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'add-site' ? (
        <Modal
          title={`Ajouter un site — ${contactTitle(modal.client)}`}
          icon={<IconGlobe />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                disabled={modal.saving}
                onClick={async () => {
                  setModal((m) => ({ ...m, saving: true, error: '' }));
                  const res = await createSiteForClient({ client_id: modal.client.id, name: modal.name, url: modal.url, send_email: Boolean(modal.send_email) });
                  if (!res.ok) {
                    setModal((m) => ({ ...m, saving: false, error: res.error || 'Erreur' }));
                    return;
                  }
                  if (res.email && res.email.ok === false) {
                    setModal({ type: 'email-warning', error: res.email.error || 'Email non envoyé' });
                    return;
                  }
                  setModal(null);
                }}
              >
                {modal.saving ? 'Création…' : 'Créer le site'}
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label" htmlFor="s-name">
              Nom du site
            </label>
            <input id="s-name" className="Input" value={modal.name} onChange={(e) => setModal((m) => ({ ...m, name: e.target.value }))} />
          </div>
          <div className="Field">
            <label className="Label" htmlFor="s-url">
              URL
            </label>
            <input id="s-url" className="Input" value={modal.url} onChange={(e) => setModal((m) => ({ ...m, url: e.target.value }))} />
            <div className="Hint">Le favicon est automatiquement proposé à partir du domaine.</div>
          </div>
          <label className="ButtonInline" style={{ gap: 10 }}>
            <input type="checkbox" checked={Boolean(modal.send_email)} onChange={(e) => setModal((m) => ({ ...m, send_email: Boolean(e.target.checked) }))} disabled={modal.saving} />
            <span>Envoyer un email au contact (optionnel)</span>
          </label>
          {modal.error ? (
            <div className="Error" role="alert">
              {modal.error}
            </div>
          ) : null}
        </Modal>
      ) : null}

      {modal?.type === 'delete-step1' ? (
        <Modal
          title="Suppression"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button type="button" className="Button" onClick={() => setModal({ type: 'delete-step2', client: modal.client })}>
                Continuer
              </button>
            </>
          }
        >
          <div className="Muted">
            Supprimer <strong>{contactTitle(modal.client)}</strong> ?
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'delete-step2' ? (
        <Modal
          title="Conservation des sites"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--ghost"
                onClick={async () => {
                  await deleteClient({ id: modal.client.id, keep_sites: true });
                  setModal(null);
                }}
              >
                Conserver les sites
              </button>
              <button
                type="button"
                className="Button"
                onClick={async () => {
                  await deleteClient({ id: modal.client.id, keep_sites: false });
                  setModal(null);
                }}
              >
                Supprimer les sites
              </button>
            </>
          }
        >
          <div className="Muted">
            Souhaites-tu conserver les sites liés à <strong>{contactTitle(modal.client)}</strong> ?
          </div>
          <div className="Hint">
            La suppression des sites ne supprime que ceux liés exclusivement à ce client (si un site est lié à plusieurs clients, il sera conservé).
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'simulate' ? (
        <Modal
          title="Simuler la vue client"
          icon={<IconUsers />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button"
                onClick={() => {
                  const chosen = (modal.sites || []).find((s) => Number(s.id) === Number(modal.siteId));
                  if (!chosen) return;
                  onSimulate?.({ client: modal.client, sites: modal.sites, siteId: chosen.id });
                  setModal(null);
                }}
              >
                Activer
              </button>
            </>
          }
        >
          <div className="Muted" style={{ marginBottom: 4 }}>
            Choisis le site à afficher pour <strong>{contactTitle(modal.client)}</strong>.
          </div>
          <div className="Field">
            <label className="Label" htmlFor="sim-site">
              Site
            </label>
            <select
              id="sim-site"
              className="Select"
              value={modal.siteId ?? ''}
              onChange={(e) => setModal((m) => ({ ...m, siteId: Number(e.target.value) }))}
            >
              {(modal.sites || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="Hint">Un cadre spécial apparaîtra pour indiquer la simulation.</div>
        </Modal>
      ) : null}
    </div>
  );
}
